import { useState, useEffect } from "react";
import api, { backendUrl } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Loader2, X, Cloud, MessageCircle, Mail, CheckCircle2 } from "lucide-react";

const LONG_FIELDS = ["NOTE_AGGIUNTIVE", "REQUISITI_IMPRESCINDIBILI", "DESCRIZIONE_IMMOBILE"];
const isDateField = (f) => /DATA|SCADENZA/.test(f);
const toISO = (s) => { const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || ""); return m ? `${m[3]}-${m[2]}-${m[1]}` : ""; };
const fromISO = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };
const TIPO_DOC_OPZIONI = ["Passaporto", "Carta d'identità", "Altro"];
const DOCNUM_PH = ["NUMERO_DOCUMENTO", "NUMERO_DOCUMENTO_IDENTITA", "NUMERO_PASSAPORTO", "N_PASSAPORTO", "DOCUMENTO_IDENTITA", "NUMERO_PERMESSO_SOGGIORNO"];
const GENERIC_DOC = ["NUMERO_DOCUMENTO", "NUMERO_DOCUMENTO_IDENTITA", "DOCUMENTO_IDENTITA"];

function docUrl(id) {
  const token = localStorage.getItem("rb_token");
  return `${backendUrl}/api/documenti/${id}/download?token=${token}`;
}

export default function DocumentoDialog({ open, onClose, clienteId, clienteNome, praticaId, preset, onGenerated }) {
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [campi, setCampi] = useState([]);
  const [values, setValues] = useState({});
  const [docNumeri, setDocNumeri] = useState({});
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const setField = (f, val) => { setValues((v) => ({ ...v, [f]: val })); setMissing((m) => m.filter((x) => x !== f)); };
  const docNumField = campi.find((c) => DOCNUM_PH.includes(c.field))?.field || null;
  const isGeneric = GENERIC_DOC.includes(docNumField);
  const docNumCampo = campi.find((c) => c.field === docNumField);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    if (preset?.template) {
      applyCampi(preset.template, preset.campi, preset.missing || [], preset.doc_numeri);
    } else {
      setTpl(null); setCampi([]); setValues({}); setMissing([]);
      api.get("/templates").then(({ data }) => setTemplates(data.templates || []))
        .catch(() => toast.error("Impossibile caricare i modelli"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyCampi = (template, campiList, miss, numeri) => {
    setTpl(template);
    setCampi(campiList || []);
    setDocNumeri(numeri || {});
    const gField = (campiList || []).find((c) => DOCNUM_PH.includes(c.field))?.field || null;
    const gIsGeneric = GENERIC_DOC.includes(gField);
    const v = {};
    (campiList || []).forEach((c) => {
      // Campo numero documento generico: vuoto finché non si sceglie il tipo
      v[c.field] = (gIsGeneric && c.field === gField) ? "" : (c.value || "");
    });
    setValues(v);
    setMissing(miss || []);
  };

  const onTipoChange = (tipo) => {
    setValues((v) => {
      const nv = { ...v, TIPO_DOCUMENTO: tipo };
      if (docNumField && isGeneric) nv[docNumField] = docNumeri[tipo] || "";
      return nv;
    });
    setMissing((m) => m.filter((x) => x !== docNumField && x !== "TIPO_DOCUMENTO"));
  };

  const selectTemplate = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.post("/documenti/genera", {
        cliente_id: clienteId, template_id: t.id, pratica_id: praticaId || null, force: false,
      });
      if (data.status === "ok") { setResult(data); onGenerated?.(data); }
      else applyCampi(data.template, data.campi, data.missing, data.doc_numeri);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento dei campi");
    } finally { setLoading(false); }
  };

  const generate = async () => {
    const stillMissing = campi.filter((c) => c.required && !String(values[c.field] || "").trim());
    if (docNumField && isGeneric && !String(values.TIPO_DOCUMENTO || "").trim()) {
      return toast.warning("Seleziona il tipo di documento.");
    }
    if (stillMissing.length) {
      setMissing(stillMissing.map((c) => c.field));
      return toast.warning("Compila i campi obbligatori evidenziati (nome, cognome e numero documento).");
    }
    const extra = { ...values };
    if (docNumField && isGeneric && values.TIPO_DOCUMENTO) {
      extra[docNumField] = `${values.TIPO_DOCUMENTO} ${values[docNumField] || ""}`.trim();
    }
    setLoading(true);
    try {
      const { data } = await api.post("/documenti/genera", {
        cliente_id: clienteId || preset?.cliente_id, template_id: tpl.id,
        pratica_id: praticaId || preset?.pratica_id || null, extra, force: true,
      });
      if (data.status === "ok") {
        setResult(data);
        toast.success("Documento generato");
        onGenerated?.(data);
      } else {
        applyCampi(data.template, data.campi, data.missing);
        toast.warning("Alcuni campi obbligatori mancano ancora.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="documento-dialog">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col border-t-2 border-[#C9A84C] shadow-lg">
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#1A2B4A]/10">
          <h3 className="font-serif text-2xl text-[#1A2B4A] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#C9A84C]" /> Genera documento
          </h3>
          <button data-testid="documento-dialog-close" onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {clienteNome && <p className="text-sm text-[#4a5568] mb-4">Cliente: <span className="text-[#1A2B4A] font-medium">{clienteNome}</span></p>}

          {result ? (
            <div data-testid="documento-result" className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-[#276749] mx-auto mb-3" />
              <p className="font-serif text-xl text-[#1A2B4A] mb-1">{result.documento.label}</p>
              <p className="text-sm text-[#4a5568] mb-5">{result.documento.filename}</p>
              <div className="flex flex-col gap-2 max-w-sm mx-auto">
                <a data-testid="result-download" href={docUrl(result.documento.id)} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 text-sm bg-[#1A2B4A] text-white px-4 py-2 rounded-sm hover:bg-[#22345c]"><FileText className="w-4 h-4" /> Scarica PDF</a>
                {result.drive_link && <a href={result.drive_link} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-sm border border-[#1A2B4A]/20 px-4 py-2 rounded-sm hover:border-[#C9A84C]"><Cloud className="w-4 h-4" /> Copia cliente su Drive</a>}
                {result.central_link && <a href={result.central_link} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-sm border border-[#1A2B4A]/20 px-4 py-2 rounded-sm hover:border-[#C9A84C]"><Cloud className="w-4 h-4" /> Copia archivio centrale</a>}
              </div>
            </div>
          ) : !tpl ? (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-3">Scegli il modello</p>
              {loading ? <div className="flex items-center gap-2 text-sm text-[#4a5568]"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</div> : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {templates.map((t) => {
                    // In attesa del nuovo generatore: resta attiva solo l'Informativa Privacy.
                    const attivo = /informativa\s*privacy/i.test(t.label || "");
                    return (
                      <button key={t.id} data-testid={`template-option-${t.id}`}
                        onClick={() => attivo && selectTemplate(t)} disabled={!attivo}
                        aria-disabled={!attivo} title={attivo ? t.label : "Modello in revisione: non disponibile"}
                        className={`text-left text-sm px-3 py-2.5 rounded-sm border flex items-center justify-between gap-2 transition-colors ${
                          attivo
                            ? "bg-[#f9f8f5] border-[#1A2B4A]/10 hover:border-[#C9A84C] cursor-pointer"
                            : "bg-[#f4f4f5] border-[#1A2B4A]/8 text-[#8a93a6] cursor-not-allowed"}`}>
                        <span className="min-w-0 truncate">{t.label}</span>
                        {!attivo && (
                          <span data-testid={`template-revisione-${t.id}`}
                            className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full bg-[#FDFAF1] text-[#7a5c12] border border-[#C9A84C]/50">
                            In revisione
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {templates.length === 0 && <p className="text-sm text-[#4a5568]">Nessun modello trovato su Drive (MODULISTICA).</p>}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#1A2B4A] font-medium">{tpl.label}</p>
                {!preset && <button onClick={() => setTpl(null)} className="text-xs text-[#4a5568] hover:text-[#C9A84C]">Cambia modello</button>}
              </div>
              <div className="bg-[#f0f7f2] border border-[#276749]/20 text-[#276749] text-xs px-3 py-2 rounded-md mb-4">
                Obbligatori solo <b>nome, cognome</b> e <b>tipo/numero del documento</b>. Gli altri campi sono facoltativi: se lasciati vuoti resteranno spazi bianchi nel PDF, compilabili a mano.
              </div>
              {missing.length > 0 && (
                <div className="bg-[#faf5e6] border border-[#C9A84C]/40 text-[#8a6d1f] text-xs px-3 py-2 rounded-md mb-4">
                  Compila i campi obbligatori evidenziati in rosso. I dati inseriti verranno salvati nell'anagrafica del cliente per i prossimi documenti.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                {docNumField && (
                  <div className="sm:col-span-2 grid sm:grid-cols-2 gap-x-4 gap-y-3 bg-[#f9f8f5] border border-[#1A2B4A]/10 rounded-md p-3">
                    <div>
                      <label className="text-xs text-[#4a5568]">Tipo documento{isGeneric && <span className="text-[#9b2c2c]">*</span>}</label>
                      <select data-testid="campo-tipo-documento" value={values.TIPO_DOCUMENTO || ""}
                        onChange={(e) => onTipoChange(e.target.value)}
                        className="w-full mt-1 px-2 py-1.5 text-sm border border-[#1A2B4A]/20 rounded-lg focus:outline-none focus:border-[#C9A84C] bg-white">
                        <option value="">— Seleziona —</option>
                        {TIPO_DOC_OPZIONI.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#4a5568]">{docNumCampo?.label || "Numero documento"}<span className="text-[#9b2c2c]">*</span></label>
                      <input data-testid={`campo-${docNumField}`} value={values[docNumField] || ""}
                        onChange={(e) => setField(docNumField, e.target.value)} placeholder="Numero del documento"
                        className={`w-full mt-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-[#C9A84C] ${missing.includes(docNumField) ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20"}`} />
                    </div>
                  </div>
                )}
                {campi.map((c) => {
                  if (c.field === docNumField) return null;
                  const isMissing = missing.includes(c.field);
                  const long = LONG_FIELDS.includes(c.field);
                  const dateF = isDateField(c.field);
                  const hint = c.field === "DATA_FIRMA" ? "modificabile" : c.source;
                  return (
                    <div key={c.field} className={long ? "sm:col-span-2" : ""}>
                      <label className="text-xs text-[#4a5568] flex items-center gap-1">
                        {c.label}{c.required && <span className="text-[#9b2c2c]">*</span>}
                        {hint === "anagrafica" && <span className="text-[10px] text-[#276749]">· anagrafica</span>}
                        {hint === "modificabile" && <span className="text-[10px] text-[#4a5568]">· modificabile</span>}
                        {hint === "auto" && <span className="text-[10px] text-[#4a5568]">· auto</span>}
                      </label>
                      {long ? (
                        <textarea data-testid={`campo-${c.field}`} rows={2} value={values[c.field] || ""}
                          onChange={(e) => setField(c.field, e.target.value)}
                          className={`w-full mt-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-[#C9A84C] resize-none ${isMissing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20"}`} />
                      ) : dateF ? (
                        <input data-testid={`campo-${c.field}`} type="date" value={toISO(values[c.field])}
                          onChange={(e) => setField(c.field, fromISO(e.target.value))}
                          className={`w-full mt-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-[#C9A84C] ${isMissing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20"}`} />
                      ) : (
                        <input data-testid={`campo-${c.field}`} value={values[c.field] || ""}
                          onChange={(e) => setField(c.field, e.target.value)}
                          className={`w-full mt-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-[#C9A84C] ${isMissing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {tpl && !result && (
          <div className="px-6 py-3 border-t border-[#1A2B4A]/10 flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
            <button data-testid="documento-generate" onClick={generate} disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Genera PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
